import { useEffect, useState, useRef } from 'react';
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Switch, Route, Redirect, useLocation, useHistory } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@components/ui/Tooltip.js';
import { useSettingsQuery } from '@features/policies/hooks/usePoliciesQuery.js';
import { useAuthStore } from '@features/auth/store/AuthStore.js';
import { useMeQuery } from '@features/auth/hooks/useAuth.js';
import { initPushNotifications } from '@services/PushNotificationService.js';

import '@ionic/react/css/core.css';
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';
import '@ionic/react/css/palettes/dark.class.css';

import '@styles/global.css';

import { AppRoutes } from '@routes/index.js';
import Sidebar from '@components/Sidebar.js';
import BottomBar from '@components/BottomBar.js';
import LoginPage from '@pages/LoginPage.js';

setupIonicReact({
  mode: 'md',
  animated: true,
  rippleEffect: true,
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: 1,
    },
  },
});

function useMinWidth(breakpoint: number) {
  const [matches, setMatches] = useState(() => window.innerWidth >= breakpoint);
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${String(breakpoint)}px)`);
    const handler = (e: MediaQueryListEvent) => {
      setMatches(e.matches);
    };
    mql.addEventListener('change', handler);
    setMatches(mql.matches);
    return () => {
      mql.removeEventListener('change', handler);
    };
  }, [breakpoint]);
  return matches;
}

function AuthenticatedShell() {
  const user = useAuthStore((s) => s.user);
  const { data: settingsData } = useSettingsQuery({ enabled: user?.role !== 'ADMIN' });
  const theme = settingsData?.data ? settingsData.data.theme : 'light';
  const history = useHistory();

  const isTablet = useMinWidth(768);
  const isDesktop = useMinWidth(1024);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  // Initialise push notifications once on native — navigates to policy/enquiry
  // detail page when the agent taps a renewal reminder notification.
  useEffect(() => {
    void initPushNotifications((path) => {
      history.push(path);
    });
  }, [history]);
  useEffect(() => {
    if (isDesktop) setSidebarCollapsed(false);
  }, [isDesktop]);

  useEffect(() => {
    let isDark = false;
    if (theme === 'dark') {
      isDark = true;
    } else if (theme === 'system') {
      isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    document.documentElement.classList.toggle('dark', isDark);
    document.documentElement.classList.toggle('ion-palette-dark', isDark);
  }, [theme]);

  const showSidebar = isTablet;
  const showBottomBar = !isTablet;
  const sidebarWidth = sidebarCollapsed ? 64 : 256;

  return (
    <div className="flex h-full w-full overflow-hidden bg-body-bg">
      {showSidebar && (
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => {
            setSidebarCollapsed((v) => !v);
          }}
          style={{ width: sidebarWidth, minWidth: sidebarWidth }}
          className="flex flex-col bg-surface border-r border-line h-full flex-shrink-0"
        />
      )}

      <div className="flex-1 flex flex-col h-full overflow-hidden relative min-w-0">
        <IonRouterOutlet id="main-content">
          <AppRoutes />
        </IonRouterOutlet>

        {showBottomBar && <BottomBar />}
      </div>
    </div>
  );
}

function AuthGate() {
  const queryClient = useQueryClient();
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const userId = user?.id;
  const location = useLocation();

  const prevAuthRef = useRef(isAuthenticated);
  const prevUserIdRef = useRef(userId);

  useEffect(() => {
    // Clear query client cache when authentication state changes, but not on initial mount
    if (prevAuthRef.current !== isAuthenticated || prevUserIdRef.current !== userId) {
      queryClient.clear();
      prevAuthRef.current = isAuthenticated;
      prevUserIdRef.current = userId;
    }
  }, [isAuthenticated, userId, queryClient]);

  // Query me API to verify session in background, using TanStack Query
  useMeQuery(isAuthenticated && user?.role !== 'ADMIN');

  if (isAuthenticated) {
    if (location.pathname === '/login' || location.pathname === '/register') {
      return <Redirect to={user?.role === 'ADMIN' ? '/users' : '/policies'} />;
    }
    return <AuthenticatedShell />;
  }

  return (
    <Switch>
      <Route exact path="/login" component={LoginPage} />
      <Route path="*" render={() => <Redirect to="/login" />} />
    </Switch>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <IonApp>
          <IonReactRouter>
            <AuthGate />
          </IonReactRouter>

          <Toaster
            position="top-right"
            richColors
            closeButton
            duration={4000}
            toastOptions={{
              style: {
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '13px',
              },
            }}
          />
        </IonApp>
      </TooltipProvider>
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
    </QueryClientProvider>
  );
}
